import { useNavigate } from 'react-router-dom'
import { BeatLoader } from 'react-spinners'
import { useCallback, useEffect, useRef, useState } from 'react'
import ErrorIcon from '@mui/icons-material/Error'
import { checkMSAuthToken, setTokenToCookie } from '../utils/cookies'
import {
  FailedToParseJsonError,
  OfficeActionError,
  SetExpiredCookieError,
} from '../errors'
import { ResultAsync } from 'neverthrow'
import {
  createOfficeDisplayDialogError,
  dialogErrorHandler,
} from '../utils/dialog_error_util'
import { emitOfficeDialiogEvent, matchDialogEvent } from '../utils/dialog_util'
import mittEmitter from '../utils/mitt'
import {
  DialogEventHandlerArgError,
  DialogEventHandlerArgSuccess,
} from '../types/dialog'
import { parseJsonSafe } from '../utils/json'
import { pipe } from 'fp-ts/lib/function'
import { isDialogMessageParentEvent } from '../utils/dialog-event-guard'
import * as O from 'fp-ts/Option'

const openAuthDialogPromise = () =>
  new Promise<Office.Dialog>((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      `${process.env.REACT_APP_DIALOG_APP}/auth`,
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(result.error)
        } else {
          result.value.addEventHandler(
            Office.EventType.DialogMessageReceived,
            (arg) => {
              emitOfficeDialiogEvent(mittEmitter, arg)
            }
          )
          result.value.addEventHandler(
            Office.EventType.DialogEventReceived,
            (arg) => {
              emitOfficeDialiogEvent(mittEmitter, arg)
            }
          )
          resolve(result.value)
        }
      }
    )
  })

const openAuthDialogResultAsync = () =>
  ResultAsync.fromPromise(openAuthDialogPromise(), (error) =>
    dialogErrorHandler(error)
  )

const AuthenticationPage = () => {
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  const [authStatus, setAuthStatus] = useState<
    | { status: 'authenticating' }
    | {
        status: 'error'
        error:
          | OfficeActionError
          | FailedToParseJsonError
          | SetExpiredCookieError
      }
  >({ status: 'authenticating' })
  const dialogRef = useRef<Office.Dialog | null>(null)

  const openAuthDialog = useCallback(() => {
    openAuthDialogResultAsync().match(
      (dialog) => {
        if (dialogRef.current) {
          dialogRef.current.close()
        }
        dialogRef.current = dialog
      },
      (err) => {
        switch (err.error.code) {
          case 12007:
            return
          default:
            setAuthStatus({ status: 'error', error: err.error })
        }
      }
    )
  }, [])

  useEffect(() => {
    const token = checkMSAuthToken()
    if (token.isOk()) {
      navigateRef.current('/', { replace: true })
    } else {
      openAuthDialog()
    }
  }, [openAuthDialog])

  useEffect(() => {
    const handleOfficeDialogErrorEvent = (
      event: DialogEventHandlerArgError
    ) => {
      setAuthStatus({
        status: 'error',
        error: createOfficeDisplayDialogError({ code: event.error }).error,
      })
    }

    const handleAuthTokenSuccess = () => {
      dialogRef.current?.close()
      navigateRef.current('/files', { replace: true })
    }

    const handleAuthTokenError = (err: SetExpiredCookieError) => {
      setAuthStatus({ status: 'error', error: err })
      dialogRef.current?.close()
    }

    const processAuthToken = (token: {
      _tag: 'RETURN_AUTH_TOKEN'
      data: string
    }) =>
      pipe(setTokenToCookie(token.data), (result) =>
        result.match(handleAuthTokenSuccess, handleAuthTokenError)
      )

    const handleJsonParseResult = (result: unknown) =>
      pipe(
        result,
        O.fromPredicate(isDialogMessageParentEvent),
        O.chain((token) =>
          pipe(
            token,
            O.fromPredicate((t) => t._tag === 'RETURN_AUTH_TOKEN'),
            O.map(processAuthToken)
          )
        )
      )

    const handleOfficeDialogSuccessEvent = (
      event: DialogEventHandlerArgSuccess
    ) => {
      const parsedResult = parseJsonSafe(event.message)

      parsedResult.match(handleJsonParseResult, (error) =>
        setAuthStatus({ status: 'error', error })
      )
    }

    const handler = matchDialogEvent(
      handleOfficeDialogErrorEvent,
      handleOfficeDialogSuccessEvent
    )

    mittEmitter.on('OfficeDialiogEvent', handler)

    return () => {
      mittEmitter.off('OfficeDialiogEvent', handler)
    }
  }, [])

  useEffect(() => {
    return () => {
      if (dialogRef.current) {
        dialogRef.current.close()
        dialogRef.current = null
      }
    }
  }, [])

  if (authStatus.status !== 'authenticating') {
    return (
      <div className="w-full h-full flex justify-center items-start">
        <div className="flex flex-col w-full justify-center items-center bg-white p-4 rounded-lg">
          <div>
            <ErrorIcon sx={{ color: 'red', fontSize: 30 }} />
          </div>
          <div className="text-sm font-bold">{authStatus.error.name}</div>
          <div className="text-sm font-semibold">
            {authStatus.error.message}
          </div>

          <div className="flex w-full justify-center mt-1">
            <button
              onClick={() => {
                setAuthStatus({ status: 'authenticating' })
                openAuthDialog()
              }}
              className="text-white bg-gradient-to-r from-teal-400 via-teal-500 to-teal-600 hover:bg-gradient-to-br focus:ring-2 focus:outline-none focus:ring-teal-300 font-medium rounded-full text-xs px-2 py-2 text-center"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex justify-center items-center">
      <div className="flex flex-col w-full justify-center items-center animate-bounce">
        <div>
          <BeatLoader size={20} color="#36d7b7" />
        </div>
        <div className="font-semibold">Authenticating user...</div>
      </div>
    </div>
  )
}

export default AuthenticationPage
